import type { ReportQuestionItem } from '@/axios/company';

/**
 * 与后端 GeoPlatform 展示名保持一致，补齐公共平台字典未包含的历史或其他区域平台。
 * 平台字典提供有效名称时仍优先使用字典，不改变其他报表的平台配置。
 */
const DEFAULT_PLATFORM_NAMES: ReadonlyMap<string, string> = new Map([
  ['DOUBAO', '字节豆包'],
  ['DOUBAO_THINKING', '豆包深思'],
  ['DOUBAO2', '字节豆包-免登录'],
  ['KIMI', 'Kimi'],
  ['DEEPSEEK', 'DeepSeek'],
  ['DEEPSEEK_THINKING', 'DeepSeek深思'],
  ['HUNYUAN', '元宝'],
  ['YUANBAO', '腾讯元宝'],
  ['YIYAN', '百度文小言'],
  ['YIYAN2', '百度文心'],
  ['TONGYI', '阿里通义'],
  ['TONGYI2', '通义千问'],
  ['BAIDU_AI', '百度AI'],
  ['VOLC', '抖音豆包'],
  ['VOLC_ARK', '火山方舟'],
  ['DEEPSEEK_API', 'DEEPSEEK API'],
  ['GROK', 'xAI'],
  ['CHATGPT', 'ChatGPT'],
  ['GEMINI', 'Gemini'],
  ['COPILOT', 'Copilot'],
  ['TONGYI2_MOBILE', '通义移动端'],
  ['DOUBAO_MOBILE', '豆包移动端'],
  ['YUANBAO_MOBILE', '元宝移动端'],
  ['DEEPSEEK_MOBILE', 'DeepSeek移动端'],
]);

/**
 * 依次解析报告、全局及默认平台名称，保留 DeepSeek 等品牌名并兼容原有两参数调用。
 * @param {string} platform 当前行或回答所属的业务平台代码。
 * @param {ReadonlyArray<{platform: string, name: string}>} preferredMappings 优先使用的平台字典，表格传当前报告 platformMappings。
 * @param {ReadonlyArray<{platform: string, name: string}>} fallbackMappings 次优先平台字典，表格传全局字典，省略时为空。
 * @returns {string} 有效字典名、已知平台默认名或未知平台原始代码。
 */
export function getReportPlatformName(
  platform: string,
  preferredMappings: ReadonlyArray<{ platform: string; name: string }>,
  fallbackMappings: ReadonlyArray<{ platform: string; name: string }> = []
): string {
  // 高优先级字典缺项、名称空白或只有平台代码时，继续查下一份字典。
  for (const mappings of [preferredMappings, fallbackMappings]) {
    const configuredName = mappings.find(item => item.platform === platform)?.name?.trim();
    if (configuredName && configuredName !== platform) {
      return configuredName;
    }
  }
  return DEFAULT_PLATFORM_NAMES.get(platform) || platform;
}

/**
 * 校验外部对话及 Markdown 链接，只允许完整的 HTTP/HTTPS 地址。
 * @param {string | null | undefined} value 原始链接。
 * @returns {string | undefined} 去除首尾空白的安全链接，非法链接返回 undefined。
 */
export function safeReportUrl(value?: string | null): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed || !/^https?:\/\//i.test(trimmed)) {
    return undefined;
  }
  try {
    const parsed = new URL(trimmed);
    return ['http:', 'https:'].includes(parsed.protocol) && parsed.hostname ? trimmed : undefined;
  } catch {
    return undefined;
  }
}

/**
 * 按分享链接优先、服务端许可兜底的规则决定问题入口。
 * @param {ReportQuestionItem} row 当前报告列表行。
 * @returns {{type: 'link', url: string} | {type: 'answer'} | {type: 'none'}} 安全跳转、回答弹窗或无入口。
 */
export function questionAction(row: ReportQuestionItem): { type: 'link'; url: string } | { type: 'answer' } | { type: 'none' } {
  if (row.shareUrl?.trim()) {
    const url = safeReportUrl(row.shareUrl);
    return url ? { type: 'link', url } : { type: 'none' };
  }
  return row.answerViewable === true ? { type: 'answer' } : { type: 'none' };
}

/**
 * 提取无时区测试日期并检查真实日历日期，避免浏览器时区转换。
 * @param {string | null | undefined} value 后端 LocalDateTime 或日期字符串。
 * @returns {string} YYYY-MM-DD；空值或非法日期为破折号。
 */
export function formatReportDate(value?: string | null): string {
  const match = value?.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}):(\d{2})(?:\.\d{1,9})?)?$/);
  if (!match) {
    return '—';
  }
  const [, yearText, monthText, dayText, hour, minute, second] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > days[month - 1] || Number(hour || 0) > 23 || Number(minute || 0) > 59 || Number(second || 0) > 59) {
    return '—';
  }
  return `${yearText}-${monthText}-${dayText}`;
}
